import React, {useContext, useEffect} from "react";
import {useTranslation} from "react-i18next";
import {AppContext} from "../../authProvider/AuthProvider";
import {useNavigate, useSearchParams} from "react-router-dom";
import {PlayersStatsApi} from "../../../services/api/PlayersStatsApi";
import {UserWithStats} from "../../../models/UserWithStats";
import {UiButton} from "../../ui-components/button/uiButton";
import {PlatformType, SelectGameMode} from "../../selectGameMode/SelectGameMode";
import {StatsStrategy} from "../../../models/stats/statsStrategy";
import {getStatsStrategy} from "../../../models/stats/statsStrategyFactory";
import {StatsStrategyClassic} from "../../../models/stats/strategy/StatsStrategyClassic";
import {UiLoader} from "../../ui-components/loader/uiLoader";


enum errorTypeEnum {
    notConnectedAndNoParam,
    invalidParam,
    userNotFound
}

export const Stats: React.FC = () => {
    const {t} = useTranslation();

    const navigate = useNavigate();
    const {currentUser} = useContext(AppContext)
    const [searchParams] = useSearchParams();

    const [errorType, setErrorType] = React.useState<errorTypeEnum | null>(null);

    const [playerId, setPlayerId] = React.useState<number | null>(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [playerStats, setPlayerStats] = React.useState<UserWithStats | null>(null);
    const [thereStatsForGameMode, setThereStatsForGameMode] = React.useState(false);

    const [statsStrategy, setStatsStrategy] = React.useState<StatsStrategy>(new StatsStrategyClassic());

    async function fetchStats(paltform: PlatformType, gameModeId: number) {
        if (playerId === null) return;

        setStatsStrategy(await getStatsStrategy(gameModeId))

        PlayersStatsApi.getPlayerStats(playerId, paltform, gameModeId)
            .then(([playerStats, thereStatsForGameMode]) => {
                if (playerStats === null) {
                    setErrorType(errorTypeEnum.userNotFound);
                    return;
                }

                setPlayerStats(playerStats);
                setThereStatsForGameMode(thereStatsForGameMode);
                setIsLoading(false);
            })
    }

    useEffect(() => {
        // Réinitialiser les états
        setErrorType(null);
        setIsLoading(true);
        setPlayerStats(null);

        let playerIdParam = searchParams.get("player-id");

        if (playerIdParam === null) {
            if (currentUser === null) {
                setErrorType(errorTypeEnum.notConnectedAndNoParam);
                setIsLoading(false);
            } else {
                setPlayerId(currentUser.id);
            }
        } else if (isNaN(+playerIdParam)) {
            setErrorType(errorTypeEnum.invalidParam);
            setIsLoading(false);
        } else {
            setPlayerId(+playerIdParam);
        }
    }, [searchParams, currentUser]);

    return (
        <div className="px-12 md:px-28 xl:px-44 pt-20 pb-12 md:pt-28 xl:pt-30 gap-8 flex flex-col h-full overflow-y-auto">
            <h1 className="text-4xl">{t("global.stats")}</h1>

            {/* error messages */}
            {(errorType !== null && playerStats === null) &&
                <div className="w-full h-full flex justify-center items-center">
                    {errorType === errorTypeEnum.notConnectedAndNoParam &&
                        <div className="flex flex-col gap-5 items-center">
                            <p className="text-xl text-center text-wrap">{t("stats.create-account-to-see-stats")}</p>

                            <UiButton
                                className="w-fit"
                                text={t("form.create-account")}
                                onClick={() => navigate("/login")}
                            />
                        </div>}

                    {errorType === errorTypeEnum.invalidParam && <p className="text-xl">
                        {t("error.given-param-is-invalid")}
                    </p>}

                    {errorType === errorTypeEnum.userNotFound && <p className="text-xl">
                        {t("global.player-not-found")}
                    </p>}
                </div>
            }

            {errorType === null && <div>
                <div className="flex flex-col gap-8 md:flex-row md:justify-between">
                    {/* pp and name */}
                    {playerStats !== null && <div className="flex flex-col  items-center gap-6 lg:flex-row">
                        <img
                            src={playerStats.getPP()}
                            alt={t("account.profile-picture")}
                            className="w-44 h-44 2xl:w-60 2xl:h-60 rounded-full lg"
                        />

                        <div className="max-lg:flex max-lg:flex-col max-lg:items-center">
                            <p className="text-2xl md:text-3xl xl:text-4xl text-center text-wrap break-all lg:text-start">{playerStats.name}</p>
                            <p className="text-text-transparent text-center lg:text-start">
                                {t("stats.joined-on-x", {date: playerStats.createdAt})}
                            </p>
                        </div>
                    </div>}

                    <SelectGameMode
                        key={playerId}
                        onChange={fetchStats}
                    />
                </div>

                {isLoading && <UiLoader isOpen={true} /> }

                {!isLoading && (playerStats !== null) && <div className="pt-12">
                    {thereStatsForGameMode ? <div className="flex flex-col gap-24 lg:flex-row lg:justify-between">
                        {/* left part */}
                        <div className="flex flex-col gap-8 w-full lg:w-1/2">
                            {statsStrategy?.leftPart(t, playerStats)}
                        </div>

                        {/* right part */}
                        <div className="flex flex-wrap justify-center pt-6 lg:pt-12 gap-6 lg:gap-10 w-full lg:w-1/2">
                            {statsStrategy?.rightPart(t, playerStats)}
                        </div>
                    </div> : <div className="w-full h-full flex justify-center items-center">
                        <p>{t("stats.no-stats-for-this-gamemode")}</p>
                    </div>}
                </div>}
            </div>}
        </div>
    )
}
